import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { io } from 'socket.io-client';

import { SOCKET_IO_URL } from '@sophocles/config';
import { handleSiteDashboardSiteSocketActions } from '@sophocles/features/entities/site';
import { addToast } from '@sophocles/features/toast';
import { getSiteRoomV2, isSiteRoomV2 } from '@sophocles/socket-io/roomNames';
import { createSocketToast, getModelHandler } from '@sophocles/socket-io/utils';

// Initialize sockets
const path = { transports: ['websocket'] };
export const mainSocket = io(SOCKET_IO_URL, path);
export const siteSocket = io(`${SOCKET_IO_URL}/sites`, path);
export const userSocket = io(`${SOCKET_IO_URL}/users`, path);

const initialState = {
  siteRoom: null,
  siteRoomV2: null,
  userRoom: null,
  socketId: null,
  actions: [],
};

const V2_LEGACY_COMMAND_MODELS = new Set(['pendingunitcommand', 'completedunitcommand']);

const notifySlice = createSlice({
  name: 'notify',
  initialState,
  reducers: {
    setSocketId: (state, action) => {
      console.debug('Setting socket ID:', action.payload);
      state.socketId = action.payload;
    },
    joinUserRoom: (state, action) => {
      userSocket.emit('join', action.payload);
      console.debug('Joining user room:', action.payload);
      state.userRoom = action.payload;
    },
    leaveUserRoom: (state, action) => {
      userSocket.emit('leave', action.payload);
      console.debug('Leaving user room:', action.payload);
      state.userRoom = null;
    },
    joinSiteRoom: (state, action) => {
      siteSocket.emit('join', action.payload);
      console.debug('Joining site room:', action.payload);
      state.siteRoom = action.payload;
    },
    leaveSiteRoom: (state, action) => {
      siteSocket.emit('leave', action.payload);
      console.debug('Leaving site room:', action.payload);
      state.siteRoom = null;
    },
    joinSiteRoomV2: (state, action) => {
      const room = getSiteRoomV2(action.payload);
      siteSocket.emit('join', room);
      console.debug('Joining site room v2:', room);
      state.siteRoomV2 = room;
    },
    leaveSiteRoomV2: (state, action) => {
      const room = action.payload == null ? state.siteRoomV2 : getSiteRoomV2(action.payload);
      if (room) {
        siteSocket.emit('leave', room);
        console.debug('Leaving site room v2:', room);
      }
      state.siteRoomV2 = null;
    },
    handleUserRoomNotification: (state, action) => {
      console.debug('Handling user room notification:', action.payload);
      state.actions.push(action.payload);
    },
    handleSiteRoomNotification: (state, action) => {
      console.debug('Handling site room notification:', action.payload);
      state.actions.push(action.payload);
    },
  },
  extraReducers: builder => {
    builder
      // eslint-disable-next-line no-use-before-define -- thunk defined below; RTK builds this case map lazily on first dispatch
      .addCase(notifyUserRoom.fulfilled, (state, action) => {
        // This ensures async behavior is handled properly
        state.actions.push(action.payload);
      })
      // eslint-disable-next-line no-use-before-define -- thunk defined below; RTK builds this case map lazily on first dispatch
      .addCase(notifySiteRoom.fulfilled, (state, action) => {
        state.actions.push(action.payload);
      });
  },
});

export const {
  setSocketId,
  joinUserRoom,
  leaveUserRoom,
  joinSiteRoom,
  leaveSiteRoom,
  joinSiteRoomV2,
  leaveSiteRoomV2,
  handleUserRoomNotification,
  handleSiteRoomNotification,
} = notifySlice.actions;

// Helper function for handling notification with model-specific update and toast
const handleNotificationWithEntityUpdateAndToast = (data, dispatch, t) => {
  const { model, op, data: notificationData, site, room, generated_at: generatedAt } = data;

  // Dispatch model-specific action using getModelHandler
  const skipLegacyCommandUpdate = isSiteRoomV2(room) && V2_LEGACY_COMMAND_MODELS.has(model);
  const entityUpdate = skipLegacyCommandUpdate
    ? null
    : model === 'site' && isSiteRoomV2(room)
      ? handleSiteDashboardSiteSocketActions
      : getModelHandler(model);
  if (entityUpdate) {
    // site_id and generated_at are envelope metadata, not entity fields — the
    // odl-app dashboard contracts deliberately omit both from their data. Pass
    // them as a separate `meta` arg (never merged into the entity payload) so the
    // dashboard handlers can resolve the site and run their monotonic
    // out-of-order guard (generated_at is the snapshot's build time). Handlers
    // whose contracts carry their own site_id (legacy, sensors, media) ignore meta.
    const meta = { siteId: site ?? room, generatedAt };
    dispatch(entityUpdate(op, notificationData, meta));
  }

  const socketToast = createSocketToast(data, t);

  // Dispatch toast if message exists
  if (socketToast != null) {
    dispatch(addToast(socketToast));
  }
};

// Enhanced notification actions with entity update and toast
export const notifyUserRoom = createAsyncThunk('notify/notifyUserRoom', async ({ t, ...data }, { dispatch }) => {
  dispatch(handleUserRoomNotification(data));
  handleNotificationWithEntityUpdateAndToast(data, dispatch, t);
});

export const notifySiteRoom = createAsyncThunk('notify/notifySiteRoom', async ({ t, ...data }, { dispatch }) => {
  dispatch(handleSiteRoomNotification(data));
  handleNotificationWithEntityUpdateAndToast(data, dispatch, t);
});

// Async thunk for installing the notification listeners once at app startup.
// SocketProvider owns room rejoin because Socket.IO reports a restored
// namespace connection through the Socket's `connect` event.
export const initializeSockets = createAsyncThunk('notify/initializeSockets', async (t, { dispatch }) => {
  // Log connection and disconnection events for mainSocket.
  mainSocket.on('connect', () => {
    console.debug('Connected to main socket');
    mainSocket.emit('get_client_id');
  });

  mainSocket.on('client_id', socket_id => {
    console.debug('Received client_id:', socket_id);
    dispatch(setSocketId(socket_id));
  });

  mainSocket.on('disconnect', reason => {
    console.debug('Disconnected from main socket:', reason);
  });

  [siteSocket, userSocket].forEach(socket => {
    socket.on('connect', () => console.debug(`Connected to ${socket.nsp}`));

    socket.on('disconnect', reason => {
      console.debug(`Disconnected from ${socket.nsp}:`, reason);
    });
  });

  siteSocket.onAny((event, data) => {
    console.debug(`Received event ${event} with data:`, data);
    if (event === 'notify') {
      dispatch(notifySiteRoom({ ...data, t }));
    }
  });
  userSocket.on('notify', data => {
    console.debug('Received user notify event:', data);
    dispatch(notifyUserRoom({ ...data, t }));
  });
});

export const selectSocketId = state => state.notify.socketId;
export const selectSiteRoom = state => state.notify.siteRoom;
export const selectSiteRoomV2 = state => state.notify.siteRoomV2;
export const selectUserRoom = state => state.notify.userRoom;
export const selectActions = state => state.notify.actions;

export default notifySlice.reducer;
